import React from 'react';
import {View} from 'react-native';
import {get} from 'lodash';

import InlineFormattedNumber from '/component/base/FormattedNumber/InlineFormattedNumber';
import Text from '/component/base/Text';
import {Reports} from '/constant/TestIDs';
import {withTranslation} from '/provider/TranslationProvider';
import {generateComponentCallback} from '/util/component.util';
import {getDateUtilDefaultInstance} from '/util/date.util';
import testProperties from '/util/testing/testProperties';

import {styles} from './ExpiringWellnessCredits.style';

interface ExpiringWellnessCreditsProps {
  tt: (key: string, value?: any) => string;
  item: {
    expiringWellnessCredits?: {
      balance: string | number;
      currencyCode: string;
      expiresOn: string;
    };
  };
}

export const ExpiringWellnessCredits: React.FC<
  ExpiringWellnessCreditsProps
> = ({tt = withTranslation.ttDefaultProp, item = null}) => {
  const expiringWellnessCredits = get(item, 'expiringWellnessCredits', {});
  const {balance, currencyCode, expiresOn} = expiringWellnessCredits;
  const dateUtil = getDateUtilDefaultInstance();

  const expiryStyle = dateUtil.isDateTodayOrPast(expiresOn)
    ? styles.expiring
    : null;

  const formattedExpiryDate = dateUtil.formatRelativeDate(expiresOn, 'MMM d');

  const expireInDays = dateUtil.getDaysToToday(expiresOn);
  const isExpiryRelative = dateUtil.isDateWithinRelativeRange(expiresOn);

  let renderedExpiry;

  // The value of `expireInDays` is "number of days from expiry until today".
  // This means if this number of greater than 0, then the expiry is in the past.
  if (expireInDays > 0) {
    if (isExpiryRelative) {
      // Already expired some times in the recent past.
      renderedExpiry = (
        <Text style={expiryStyle} allowFontScaling maxFontSizeMultiplier={2}>
          {tt(
            'expiry.past.relative',
            generateComponentCallback({
              component: <>Expired {formattedExpiryDate}</>,
            }),
          )}
        </Text>
      );
    } else {
      // Already expired some times in the distant past.
      renderedExpiry = (
        <Text style={expiryStyle} allowFontScaling maxFontSizeMultiplier={2}>
          {tt(
            'expiry.past.date',
            generateComponentCallback({
              component: <>Expired on {formattedExpiryDate}</>,
            }),
          )}
        </Text>
      );
    }
  } else {
    if (isExpiryRelative) {
      // Expiring some times in the near future.
      renderedExpiry = (
        <Text style={expiryStyle} allowFontScaling maxFontSizeMultiplier={2}>
          {tt(
            'expiry.future.relative',
            generateComponentCallback({
              component: <>Expires {formattedExpiryDate}</>,
            }),
          )}
        </Text>
      );
    } else {
      // Expiring some times in the distant future.
      renderedExpiry = (
        <Text style={expiryStyle} allowFontScaling maxFontSizeMultiplier={2}>
          {tt(
            'expiry.future.days',
            generateComponentCallback({
              component: <>Expires in {expireInDays * -1} days</>,
            }),
          )}
        </Text>
      );
    }
  }

  return (
    <View
      style={styles.inline}
      {...testProperties(Reports.reportClientExpiringWellnessCredits, true)}>
      <InlineFormattedNumber
        testID={Reports.reportClientExpiringWellnessCreditsAmount}
        style={styles.balance}
        value={balance}
        decimalPlaces={2}
        currencyCode={currencyCode}
      />
      <Text style={styles.dotBefore} allowFontScaling maxFontSizeMultiplier={2}>
        {' '}
        .{' '}
      </Text>
      <View style={styles.inline}>
        <Text
          style={expiryStyle}
          allowFontScaling
          maxFontSizeMultiplier={2}
          {...testProperties(Reports.reportClientExpiringWellnessCreditsDate)}>
          {renderedExpiry}
        </Text>
      </View>
    </View>
  );
};

export default withTranslation('report.record.expiringWellnessCredits')(
  ExpiringWellnessCredits,
);
